if (!customElements.get('pickup-availability')) {
  class PickupAvailability extends HTMLElement {
    constructor() {
      super();
      if (!this.hasAttribute('available')) return;

      this.refreshHandler = this.handleRefreshBtnClick.bind(this);
      this.unavailableHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
      this.defaultStoreName = this.dataset.defaultStoreName;
      this.translations = JSON.parse(this.dataset.translations || '{}');

      this.getAvailability(this.dataset.variantId);
    }

    /**
     * Gets translation for a given key
     * @param {string} key - Translation key
     * @param {Object} params - Parameters for the translation
     * @returns {string} Translated string
     */
    getTranslation(key, params = {}) {
      let translation = this.translations[key] || key;

      // Replace parameters
      Object.entries(params).forEach(([param, value]) => {
        const placeholder = `{{ ${param} }}`;
        // Create a temporary div to decode HTML entities
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = translation;
        translation = tempDiv.textContent;
        // Replace the placeholder with the value
        translation = translation.replace(placeholder, value);
      });

      return translation;
    }

    /**
     * Handles 'click' events on the refresh button.
     */
    handleRefreshBtnClick() {
      this.getAvailability(this.dataset.variantId);
    }

    /**
     * Gets pick up availability for the current variant.
     * @param {string} variantId - Current variant id.
     */
    async getAvailability(variantId) {
      let { rootUrl } = this.dataset;
      if (!rootUrl.endsWith('/')) rootUrl = `${rootUrl}/`;

      try {
        const response = await fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        this.renderPickupInfo(tmpl.content.querySelector('.shopify-section'));
      } catch (error) {
        const refreshBtn = this.querySelector('.js-refresh');
        if (refreshBtn) refreshBtn.removeEventListener('click', this.refreshHandler);
        this.renderUnavailableMsg();
      }
    }

    /**
     * Gets the availability status text for a location
     * @param {Element} locationElement - The location element
     * @returns {Object} Object containing availability status and time
     */
    getLocationAvailabilityInfo(locationElement) {
      const statusElement = locationElement.querySelector('.flex p');
      const timeElement = locationElement.querySelector('.flex p:nth-of-type(2)');

      // Check availability by looking at the icon - unavailable locations have an X icon
      const hasUnavailableIcon = locationElement.querySelector('.pickup-icon svg path[d*="M5 19 19 5"]');
      const isAvailable = !hasUnavailableIcon;

      return {
        status: statusElement ? statusElement.textContent.trim() : '',
        time: timeElement ? timeElement.textContent.trim() : '',
        isAvailable: isAvailable
      };
    }

    /**
     * Sets up drawer event listeners
     * @param {Element} drawer - The drawer element
     * @param {Element} trigger - The element that triggers the drawer
     */
    setupDrawerListeners(drawer, trigger) {
      if (!drawer || !trigger) return;

      // Remove any existing listeners
      const newTrigger = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(newTrigger, trigger);

      // Add new listener
      newTrigger.addEventListener('click', (event) => {
        event.preventDefault();
        drawer.open(newTrigger);
      });
    }

    /**
     * Renders the pick up info.
     * @param {string} sectionHtml - Fetched section HTML.
     */
    renderPickupInfo(sectionHtml) {
      // Remove any existing drawer
      const existingDrawer = document.querySelector('.js-pickup-drawer');
      if (existingDrawer) {
        existingDrawer.remove();
      }

      if (!sectionHtml.querySelector('.pickup-status')) {
        this.innerHTML = '';
        this.removeAttribute('available');
        return;
      }

      this.setAttribute('available', '');

      // Clone the pickup status and update its content
      const pickupStatus = sectionHtml.querySelector('.pickup-status').cloneNode(true);

      // Sort locations in the drawer
      const newDrawer = sectionHtml.querySelector('.js-pickup-drawer').cloneNode(true);

      if (this.defaultStoreName) {
        // Sort locations in the drawer
        const locationsList = newDrawer.querySelector('ul[role="list"]');
        if (locationsList) {
          const locations = Array.from(locationsList.children);
          const defaultLocation = locations.find(loc =>
            loc.querySelector('h3').textContent.trim() === this.defaultStoreName
          );

          // Update all locations in the drawer to use proper translations
          locations.forEach(location => {
            const locationName = location.querySelector('h3').textContent.trim();
            const statusParagraph = location.querySelector('.flex p');

            if (statusParagraph) {
              // Use the existing method to get availability info
              const availabilityInfo = this.getLocationAvailabilityInfo(location);

              const translationKey = availabilityInfo.isAvailable
                ? 'pick_up_available_at_html'
                : 'pick_up_unavailable_at_html';

              // Get the raw translation and replace placeholder
              let translatedText = this.translations[translationKey] || translationKey;
              translatedText = translatedText.replace(/\{\{\s*location_name\s*\}\}/g, locationName);

              // Update the status text
              statusParagraph.innerHTML = translatedText;
            }
          });

          if (defaultLocation) {
            // Get the availability status and time from the default location
            const defaultLocationName = defaultLocation.querySelector('h3').textContent.trim();
            const availabilityInfo = this.getLocationAvailabilityInfo(defaultLocation);

            // Update the main pickup status text and icon
            const statusText = pickupStatus.querySelector('p.mb-0');
            const pickupIcon = pickupStatus.querySelector('.pickup-icon');

            if (statusText) {
              // Create the new status text with the store name in bold
              const translationKey = availabilityInfo.isAvailable
                ? 'pick_up_available_at_html'
                : 'pick_up_unavailable_at_html';

              // Get the raw translation
              let translatedText = this.translations[translationKey] || translationKey;

              // Replace the placeholder directly in the string (including any surrounding strong tags)
              translatedText = translatedText.replace(/\{\{\s*location_name\s*\}\}/g, defaultLocationName);

              // Set the HTML - this will automatically decode HTML entities
              statusText.innerHTML = translatedText;
            }

            if (pickupIcon) {
              // Get the correct icon from the default location
              const defaultLocationIcon = defaultLocation.querySelector('.pickup-icon').cloneNode(true);
              pickupIcon.innerHTML = '';
              pickupIcon.appendChild(defaultLocationIcon);
            }

            // Update the pickup time if it exists and location is available
            const timeText = pickupStatus.querySelector('p.text-sm');
            if (timeText && availabilityInfo.time && availabilityInfo.isAvailable) {
              timeText.textContent = availabilityInfo.time;
            } else if (timeText) {
              timeText.remove();
            }

            // Move default location to the top of the drawer list
            // First remove it from its current position
            defaultLocation.remove();
            // Then add it at the top
            locationsList.insertBefore(defaultLocation, locationsList.firstChild);
          }
        }
      }

      // Update the main element with the modified pickup status
      this.innerHTML = pickupStatus.outerHTML;

      // Add the modified drawer to the document
      document.body.appendChild(newDrawer);

      // Setup drawer event listeners
      const showDetailsBtn = this.querySelector('.js-show-pickup-info');
      if (showDetailsBtn) {
        this.setupDrawerListeners(newDrawer, showDetailsBtn);
      }
    }

    /**
     * Renders a 'pick up unavailable' message.
     */
    renderUnavailableMsg() {
      this.innerHTML = '';
      this.appendChild(this.unavailableHtml);
      this.querySelector('.js-refresh').addEventListener('click', this.refreshHandler);
    }
  }

  customElements.define('pickup-availability', PickupAvailability);
}
